import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Checkbox, MenuItem, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { formatDate, formatDateTime } from "../../utils/portalHelpers";
import {
  EmptyState,
  ErrorPanel,
  LoadingPanel,
  PageHeader,
  Panel,
  StatusChip,
  statusTone,
} from "../../components/ui";
import { partnerQueryKeys } from "../../api/queryKeys";
import {
  fetchPartnerTask,
  postPartnerTaskComment,
  setPartnerSubtaskCompleted,
  updatePartnerTaskStatus,
} from "../../api/endpoints/tasks";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import {
  STATUSES,
  STATUS_LABELS,
  canTick,
  fileLabel,
  subtaskProgress,
  isMyComment,
} from "./taskHelpers";
import AttachmentItem from "components/Attachments/AttachmentItem";

const signedInUser = () => {
  try {
    const detail = JSON.parse(localStorage.getItem("userDetail") || "{}");
    return { name: detail?.name || "", email: detail?.email || "" };
  } catch {
    return { name: "", email: "" };
  }
};

const initialsOf = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "?";

const errorText = (error, fallback) =>
  error?.response?.data?.errorMessage || error?.response?.data?.message || fallback;

/**
 * One task, as the person doing it sees it: what it says, its checklist, its files and the
 * discussion. Everything editable here is something the server already lets a participant do —
 * move the status, tick their own subtask, post a comment. The office owns the rest.
 */
const TaskDetailPage = () => {
  const { taskId } = useParams();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");

  const {
    data: task,
    isLoading,
    isError,
    refetch,
  } = usePartnerQuery(partnerQueryKeys.task(taskId), () => fetchPartnerTask(taskId));

  const refresh = () => {
    refetch();
    queryClient.invalidateQueries(partnerQueryKeys.all);
  };

  const move = useMutation(updatePartnerTaskStatus, {
    onSuccess: () => {
      toast.success("Status updated");
      refresh();
    },
    onError: (error) => toast.error(errorText(error, "That status change was refused.")),
  });

  const tick = useMutation(setPartnerSubtaskCompleted, {
    onSuccess: () => refresh(),
    onError: (error) => toast.error(errorText(error, "That subtask is not yours to change.")),
  });

  const comment = useMutation(postPartnerTaskComment, {
    onSuccess: () => {
      setDraft("");
      refresh();
    },
    onError: (error) => toast.error(errorText(error, "Your message was not posted.")),
  });

  if (isLoading) return <LoadingPanel label="Loading this task…" />;
  if (isError || !task) {
    return (
      <ErrorPanel
        title="We couldn't open this task"
        description="It may have been reassigned. Go back to your task list and try again."
        action={
          <Link
            to="/partner/tasks"
            className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white no-underline"
          >
            Back to tasks
          </Link>
        }
      />
    );
  }

  const progress = subtaskProgress(task.subtasks);
  const files = task.attachments || [];
  const comments = task.comments || [];
  // Who is signed in, to set their own comments apart from the office's.
  const me = signedInUser();
  const collaborators = task.collaborators || [];

  return (
    <div>
      <PageHeader
        eyebrow={
          <Link to="/partner/tasks" className="text-teal-700 no-underline">
            ← Tasks
          </Link>
        }
        title={task.title || "Untitled task"}
        subtitle={
          [
            task.assignedByName ? `From ${task.assignedByName}` : null,
            task.dueDate ? `Due ${formatDate(task.dueDate)}` : null,
            task.priority || null,
            task.module || null,
          ]
            .filter(Boolean)
            .join(" · ") || undefined
        }
        actions={
          <>
            <StatusChip tone={statusTone(task.status)}>
              {STATUS_LABELS[task.status] || task.status || "—"}
            </StatusChip>
            <TextField
              select
              size="small"
              label="Status"
              value={task.status || ""}
              disabled={move.isLoading}
              onChange={(event) => move.mutate({ taskId: task.id, status: event.target.value })}
              sx={{ minWidth: 160 }}
            >
              {STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {STATUS_LABELS[value]}
                </MenuItem>
              ))}
            </TextField>
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 flex flex-col gap-4">
          <Panel title="Details">
            {task.description ? (
              <p className="text-sm text-slate-700 whitespace-pre-wrap m-0">{task.description}</p>
            ) : (
              <p className="text-sm text-slate-400 m-0">No description was added.</p>
            )}
            <dl className="grid gap-3 sm:grid-cols-2 mt-4 mb-0">
              {task.clientId ? (
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Client
                  </dt>
                  <dd className="m-0 mt-0.5 text-sm">
                    <Link
                      to={`/partner/clients/${task.clientId}`}
                      className="font-medium text-teal-700 no-underline"
                    >
                      {task.clientName || "View client"}
                    </Link>
                  </dd>
                </div>
              ) : null}
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Assigned to
                </dt>
                <dd className="m-0 mt-0.5 text-sm text-slate-800">{task.assigneeName || "—"}</dd>
              </div>
              {collaborators.length ? (
                <div className="sm:col-span-2">
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Also on this task
                  </dt>
                  <dd className="m-0 mt-0.5 text-sm text-slate-800">{collaborators.join(", ")}</dd>
                </div>
              ) : null}
            </dl>
          </Panel>

          <Panel title={`Subtasks · ${progress.label}`}>
            {progress.total === 0 ? (
              <p className="text-sm text-slate-400 m-0">This task has no checklist.</p>
            ) : (
              <ul className="list-none p-0 m-0">
                {task.subtasks.map((subtask) => (
                  <li
                    key={subtask.id}
                    className="flex items-start gap-2 py-2 border-b border-slate-100 last:border-0"
                  >
                    <Checkbox
                      size="small"
                      checked={Boolean(subtask.completed)}
                      disabled={!canTick(subtask) || tick.isLoading}
                      onChange={(event) =>
                        tick.mutate({
                          taskId: task.id,
                          subtaskId: subtask.id,
                          completed: event.target.checked,
                        })
                      }
                      inputProps={{ "aria-label": subtask.title || "Subtask" }}
                    />
                    <div className="min-w-0 pt-1">
                      <p
                        className={`text-sm m-0 ${
                          subtask.completed ? "text-slate-400 line-through" : "text-slate-900"
                        }`}
                      >
                        {subtask.title || "Untitled subtask"}
                      </p>
                      <p className="text-xs text-slate-500 m-0 mt-0.5">
                        {subtask.assigneeName || "Unassigned"}
                        {subtask.deadline ? ` · due ${formatDate(subtask.deadline)}` : ""}
                        {canTick(subtask) ? " · yours" : ""}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Discussion">
            <form
              className="flex flex-col gap-2 mb-4"
              onSubmit={(event) => {
                event.preventDefault();
                const text = draft.trim();
                if (!text) return;
                comment.mutate({ taskId: task.id, text });
              }}
            >
              <TextField
                multiline
                minRows={2}
                size="small"
                placeholder="Write a message about this task…"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                inputProps={{ maxLength: 4000 }}
              />
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={!draft.trim() || comment.isLoading}
                  className="rounded-lg bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                >
                  {comment.isLoading ? "Posting…" : "Post"}
                </button>
              </div>
            </form>

            {comments.length === 0 ? (
              <EmptyState
                title="No messages yet"
                description="Ask the office a question here and it stays attached to this task."
              />
            ) : (
              <ul className="list-none p-0 m-0 flex flex-col gap-3">
                {comments.map((entry) => {
                  const mine = isMyComment(entry, me);
                  console.log(mine);
                  const author = mine ? "You" : entry.author || "The office";
                  return (
                    <li key={entry.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`}>
                      <span
                        aria-hidden="true"
                        className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                          mine ? "bg-teal-700 text-white" : "bg-slate-200 text-slate-700"
                        }`}
                      >
                        {initialsOf(mine ? me.name || "You" : entry.author || "Office")}
                      </span>
                      <div
                        className={`flex max-w-[85%] flex-col ${
                          mine ? "items-end" : "items-start"
                        }`}
                      >
                        <p className="text-[11px] m-0 text-slate-500">
                          <span className="font-semibold text-slate-700">{author}</span>
                          {" · "}
                          {formatDateTime(entry.createdAt)}
                        </p>
                        <div
                          className={`mt-1 rounded-2xl px-3 py-2 ${
                            mine
                              ? "rounded-tr-md bg-teal-700 text-white"
                              : "rounded-tl-md border border-slate-200 bg-white text-slate-800"
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap m-0">{entry.text}</p>
                          {(entry.attachments || []).length ? (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {entry.attachments.map((url) => (
                                <AttachmentItem
                                  key={url}
                                  url={url}
                                  renderFile={(fileUrl) => (
                                    <a
                                      href={fileUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className={`text-xs font-semibold underline ${
                                        mine ? "text-white" : "text-teal-700"
                                      }`}
                                    >
                                      Attachment
                                    </a>
                                  )}
                                />
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>
        </div>

        <Panel title={`Files · ${files.length}`} className="h-fit">
          {files.length === 0 ? (
            <p className="text-sm text-slate-400 m-0">Nothing attached to this task.</p>
          ) : (
            <ul className="list-none p-0 m-0 flex flex-col gap-2">
              {files.map((file) => (
                <li key={file.url}>
                  <AttachmentItem
                    url={file.url}
                    renderFile={() => (
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        className="block truncate rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-teal-700 no-underline"
                      >
                        {fileLabel(file)}
                      </a>
                    )}
                  />
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default TaskDetailPage;
